/**
 * useAuthProviders Hook
 * Standardized API hooks for public authentication provider discovery.
 */

import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook } from './factories';

interface PublicAuthProvider {
  provider_type: string;
  is_primary: boolean;
  login_url: string;
  display_name: string;
  method: 'form' | 'redirect';
}

interface PublicAuthProvidersResponse {
  providers: PublicAuthProvider[];
  total: number;
}

interface EntraConfig {
  tenant_id: string;
  client_id: string;
  redirect_uri?: string;
}

/**
 * Get public auth providers for login (no authentication required)
 */
export const usePublicAuthProviders = createQueryHook<PublicAuthProvidersResponse, void>({
  queryKey: () => ['auth-providers', 'public'] as const,
  queryFn: (apiClient) =>
    apiClient.getAvailableAuthProviders().then(response => response.data),
  logContext: 'GET /api/auth-providers',
  staleTime: QUERY_STALE_TIME.LONG,
  onSuccess: (data) => {
    log.api.response('GET', '/api/auth-providers', 200, {
      providerCount: data?.providers?.length ?? 0
    });
  },
});

/**
 * Get Entra configuration for MSAL initialization (no authentication required)
 */
export const useEntraConfig = createQueryHook<EntraConfig, void>({
  queryKey: () => ['auth-providers', 'entra-config'] as const,
  queryFn: (apiClient) =>
    apiClient.getEntraConfig().then(response => response.data),
  logContext: 'GET /api/auth/entra/config',
  staleTime: QUERY_STALE_TIME.LONG,
  onSuccess: (data) => {
    log.api.response('GET', '/api/auth/entra/config', 200, {
      tenantId: data?.tenant_id
    });
  },
});
